import type { AxiosInstance } from 'axios';

export interface HealthResponse {
  status: string;
  version: string;
  semver: string;
  build_id: string | null;
}

export interface ExceptionRow {
  id: string;
  state: string;
  pipelineStageKey:
    | 'ingestion'
    | 'ocr'
    | 'classification'
    | 'duplicate'
    | 'matching'
    | 'validation'
    | 'draft'
    | 'confirm'
    | 'approval'
    | 'acknowledgement'
    | 'resolved';
  exception: boolean;
  closeReason: string | null;
  salesOrderNumber: string | null;
  customerName: string;
  customerNumber: string | null;
  poNumber: string;
  productCode: string | null;
  requestedShippingDate: string | null;
  requestedDeliveryDate: string | null;
  totalOrderValue: number | null;
  receivedAt: string;
  warnings: readonly string[];
  warningDetails?: readonly QueueWarning[];
  emailSubject: string | null;
  emailSender: string | null;
  emailReceivedAt: string | null;
  owner: { email: string; source: 'd365' | 'email_recipient' | 'email_forwarder' } | null;
  paymentTermsCode: string | null;
  customerDefaultPaymentTermsCode: string | null;
  d365ResolvedPaymentTermsCode?: string | null;
  assetPdfUrl: string | null;
  fileUrl: string | null;
  fileExtension: string | null;
  uploadedFileName: string | null;
  fields: readonly ExceptionField[];
  feedbackNote: string | null;
  lastTransitionedBy: string | null;
  lastTransitionedAt: string | null;
}

export type QueueRow = ExceptionRow;

export const QUEUE_GROUPING_FIELDS = ['email', 'customer', 'po', 'salesOrder'] as const;

export type QueueGroupingField = (typeof QUEUE_GROUPING_FIELDS)[number];

export interface QueueViewConfig {
  groupingEnabled: boolean;
  groupingField: QueueGroupingField;
  groupsDefaultExpanded: boolean;
}

export interface QueueWarning {
  id: string;
  message: string;
  severity: 'warning' | 'error';
  category: 'informative' | 'obstructive';
  stage: ExceptionRow['pipelineStageKey'] | null;
  substepId: string | null;
  fieldKeys: readonly string[];
}

export interface ExecutionLogEntry {
  id: string;
  stage: string;
  stageKey:
    | 'ingestion'
    | 'ocr'
    | 'classification'
    | 'duplicate'
    | 'matching'
    | 'validation'
    | 'draft'
    | 'validating_in_d365'
    | 'confirm'
    | 'approval'
    | 'creating_sales_order'
    | 'acknowledgement'
    | 'resolved';
  substepId?: string | null;
  fromState: string;
  toState: string;
  reason: string | null;
  detail: string | null;
  latencyMs: number;
  modelVersion: string | null;
  emittedAt: string;
}

export type TimelineStageKey = ExecutionLogEntry['stageKey'];

export interface StageDetailStep {
  id?: string;
  title: string;
  detail: string | null;
  tone: 'info' | 'success' | 'warning' | 'danger';
}

export interface StageDetailArtifact {
  substepId?: string | null;
  title: string;
  format: 'text' | 'json' | 'markdown';
  content: string;
}

export interface StageLlmCall {
  id: string;
  substepId?: string | null;
  substep: string | null;
  provider: string;
  modelVersion: string | null;
  systemPrompt: string;
  userPrompt: string;
  rawResponse: string;
  parsedResponse: string | null;
  producedAt: string;
}

export interface StageDetail {
  key: TimelineStageKey;
  label: string;
  status: 'completed' | 'current' | 'upcoming';
  summary: string;
  unavailableReason: string | null;
  llmTraceMissingReason: string | null;
  executionLogs: readonly ExecutionLogEntry[];
  internalSteps: readonly StageDetailStep[];
  artifacts: readonly StageDetailArtifact[];
  llmCalls: readonly StageLlmCall[];
}

export interface ExceptionDetailRow extends ExceptionRow {
  executionLogs: readonly ExecutionLogEntry[];
  stageDetails: Record<TimelineStageKey, StageDetail>;
  ocrMarkdown: string | null;
  ocrMetadata: string | null;
  ocrModelVersion: string | null;
  lineItems: readonly ExceptionLineItem[];
  unitOptions: readonly UnitOption[];
}

export interface ProductOption {
  id: string;
  itemNumber: string;
  name: string;
  display: string;
  productLifecycleStateId?: string | null;
  isObsolete?: boolean;
}

export interface CustomerOption {
  id: string;
  customerNumber: string;
  name: string;
  display: string;
  role?: CustomerAccountRole;
  address?: CustomerAddress;
  invoiceAccount?: string | null;
  invoiceAddress?: CustomerAddress;
  onHoldStatus?: string | null;
  isStoppedAll?: boolean;
}

export type CustomerAccountRole = 'ship_to' | 'bill_to';

export interface CustomerAddress {
  description: string | null;
  street: string | null;
  city: string | null;
  stateOrProvince: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface ProductCandidate {
  id: string;
  itemNumber: string;
  name: string;
  confidence: number;
  display: string;
}

export interface UnitOption {
  symbol: string;
  display: string;
}

export interface ManualLineAddition {
  id: string;
  productCode: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice?: number | null;
  currency?: string | null;
  reason: string;
}

export interface ExceptionLineItem {
  manualLineId?: string | null;
  manualAddedBy?: string | null;
  manualAddedAt?: string | null;
  lineNumber: number;
  supplierPartNumber: string | null;
  itemCode: string | null;
  description: string | null;
  quantity: number | null;
  unitOfMeasure: string | null;
  extractedUnitValue?: string | null;
  matchedUnitValue?: string | null;
  lineTotal: number | null;
  matchedProductCode?: string | null;
  matchedProductLabel?: string | null;
  matchedProductDisplay?: string | null;
  matchedProductLifecycleStateId?: string | null;
  matchedProductIsObsolete?: boolean;
  productCandidates?: readonly ProductCandidate[];
  needsReview?: boolean;
  reviewMessage?: string | null;
  poUnitPrice?: number | null;
  poUnitPriceCurrency?: string | null;
  d365UnitPrice?: number | null;
  d365UnitPriceCurrency?: string | null;
  priceStatus?: LinePriceStatus;
  priceReviewMessage?: string | null;
}

export type LinePriceStatus =
  | 'match'
  | 'mismatch'
  | 'po_missing'
  | 'po_zero'
  | 'd365_missing'
  | 'pending'
  | 'not_comparable';

export interface ExceptionField {
  key: string;
  label: string;
  value: string;
  extractedValue?: string | null;
  matchedValue?: string | null;
  matchedDisplayValue?: string | null;
  matchedOptions?: readonly { value: string; label: string }[] | null;
  matchedCandidates?:
    | readonly {
        id: string;
        value: string;
        label: string;
        confidence: number;
        role?: CustomerAccountRole;
        address?: CustomerAddress;
        invoiceAccount?: string | null;
        invoiceAddress?: CustomerAddress;
        onHoldStatus?: string | null;
        isStoppedAll?: boolean;
      }[]
    | null;
  needsReview?: boolean;
  reviewMessage?: string | null;
  confidence: number | null;
  editable: boolean;
}

export interface DeliveryReview {
  customerNumber: string | null;
  dataAreaId: string | null;
  syncedAt: string | null;
  stale: boolean;
  revision: string;
  terms: DeliverySelection;
  mode: DeliverySelection;
}

interface DeliverySelection {
  valid: boolean;
  defaultValue: string | null;
  value: string | null;
  extractedValue: string | null;
  source: 'customer' | 'operator';
  needsReview: boolean;
  message: string | null;
}

export interface DraftRow {
  deliveryReview?: DeliveryReview;
  requestedDateReviewWarnings?: readonly { fieldPath: string; message: string }[];
  id: string;
  customerName: string;
  poNumber: string;
  salesOrderNumber: string | null;
  state: string;
  totalLines: number;
  totalValue: number | null;
  requestedShippingDate: string | null;
  requestedDeliveryDate: string | null;
  requestedDatesSource?: 'approval' | 'extraction';
  shipToName: string | null;
  shipToAddress: string | null;
  paymentTermsCode: string | null;
  customerDefaultPaymentTermsCode: string | null;
  d365ResolvedPaymentTermsCode?: string | null;
  paymentTermsOptions?: readonly { value: string; label: string }[];
  paymentTermsMismatch: boolean;
  owner: { email: string; source: 'd365' | 'email_recipient' | 'email_forwarder' } | null;
  shippingSiteId: string | null;
  lineItems: readonly {
    lineNumber: number;
    itemCode: string | null;
    description: string | null;
    quantity: number | null;
    unitOfMeasure: string | null;
    lineTotal: number | null;
    salesPrice: number | null;
    salesPriceCurrency: string | null;
    poUnitPrice: number | null;
    poUnitPriceCurrency: string | null;
    priceStatus: LinePriceStatus;
    priceReviewMessage: string | null;
  }[];
  warnings: readonly string[];
  readyAt: string;
}

export interface FeedbackEntry {
  id: string;
  stagingRowId: string | null;
  lane: 'captured' | 'in_training' | 'applied' | 'superseded';
  summary: string;
  field: string;
  operator: string;
  capturedAt: string;
  /** ADO #700 follow-up: order context captured at correction time.
   * NULL for legacy rows captured before the snapshot column existed. */
  orderSnapshot: FeedbackOrderSnapshot | null;
}

export interface FeedbackOrderSnapshot {
  customerName: string | null;
  customerNumber: string | null;
  supplierName: string | null;
  supplierAddress: string | null;
  shipToName: string | null;
  shipToAddress: string | null;
  shipToContact: string | null;
  paymentTermsText: string | null;
  deliveryTermsText: string | null;
  deliveryModeText: string | null;
  requestedDeliveryDate: string | null;
  poNumber: string | null;
  lineItems: Array<{
    lineIndex: number;
    itemCode: string | null;
    supplierPartNumber: string | null;
    description: string | null;
    quantity: number | null;
    unitOfMeasure: string | null;
  }>;
  capturedAt: string;
  appVersion: string;
}

export interface ConfigVersion {
  id: string;
  activatedAt: string | null;
  createdAt: string;
  author: string;
  description: string;
  active: boolean;
  /** Tunables snapshot persisted with this version. Lets the admin preview
   * what activating an older version would actually change. Null when the
   * backend could not parse the stored payload. */
  tunables: AdminTunables | null;
}

export interface AdminTunables {
  classifierThreshold: number;
  customerMatchThreshold: number;
  productMatchThreshold: number;
  defaultSiteId: string | null;
  defaultLegalEntityId: string | null;
  feedbackHintFloor: number;
  feedbackHintWeight: number;
  feedbackFallbackFloor: number;
  /**
   * When true the duplicate-detection worker does not close exact or
   * suspected duplicates. The row continues through legal-entity matching
   * with an informative warning ("This document was flagged as a
   * duplicate but was processed anyway") attached.
   */
  acceptDuplicatedDocuments: boolean;
  /**
   * When true (default), the site-matching worker consults the feedback
   * index whenever the 4-step rule fails and uses a confident operator
   * feedback as the resolved site. Toggle off to revert to the
   * rule-only behaviour.
   */
  siteFeedbackFallbackEnabled: boolean;
  /**
   * When true, the queue renders Admin-selected group parent rows. When
   * false, the queue keeps the flat pre-grouping row presentation.
   */
  queueGroupingEnabled: boolean;
  /**
   * Queue grouping selected by Admin. Operators do not change this from
   * the queue screen; the queue only applies the active configuration.
   */
  queueGroupingField: QueueGroupingField;
  /**
   * Initial expanded/collapsed state for queue groups.
   */
  queueGroupsDefaultExpanded: boolean;
}

export interface CustomerSyncSample {
  id: string;
  customerName: string;
  customerNumber: string;
  syncedAt: string;
}

export interface CustomerSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedCustomerCount: number;
  latestIndexedAt: string | null;
  samples: readonly CustomerSyncSample[];
}

export interface ProductSyncSample {
  id: string;
  itemNumber: string;
  productName: string;
  syncedAt: string;
}

export interface ProductSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedProductCount: number;
  latestIndexedAt: string | null;
  samples: readonly ProductSyncSample[];
}

export interface LegalEntitySyncSample {
  id: string;
  legalEntityId: string;
  legalEntityName: string;
  syncedAt: string;
}

export interface LegalEntitySyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedLegalEntityCount: number;
  latestIndexedAt: string | null;
  samples: readonly LegalEntitySyncSample[];
}

export interface SiteSyncSample {
  id: string;
  siteId: string;
  siteName: string;
  syncedAt: string;
}

export interface SiteSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedSiteCount: number;
  latestIndexedAt: string | null;
  samples: readonly SiteSyncSample[];
}

export interface PaymentTermsSyncSample {
  id: string;
  code: string;
  label: string;
  syncedAt: string;
}

export interface PaymentTermsSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedPaymentTermsCount: number;
  latestIndexedAt: string | null;
  samples: readonly PaymentTermsSyncSample[];
}

export interface DeliveryTermsSyncSample {
  id: string;
  code: string;
  label: string;
  syncedAt: string;
}

export interface DeliveryTermsSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedDeliveryTermsCount: number;
  latestIndexedAt: string | null;
  samples: readonly DeliveryTermsSyncSample[];
}

export interface DeliveryModeSyncSample {
  id: string;
  code: string;
  label: string;
  syncedAt: string;
}

export interface DeliveryModeSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedDeliveryModeCount: number;
  latestIndexedAt: string | null;
  samples: readonly DeliveryModeSyncSample[];
}

export interface UnitOfMeasureSyncSample {
  id: string;
  symbol: string;
  description: string;
  syncedAt: string;
}

export interface UnitOfMeasureSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  indexedUnitOfMeasureCount: number;
  latestIndexedAt: string | null;
  samples: readonly UnitOfMeasureSyncSample[];
}

export interface EmbeddingSyncStats {
  lastSyncAt: string | null;
  lastSyncDurationMs: number | null;
  lastSyncRecordCount: number | null;
  lastSyncError: string | null;
  pendingCustomerCount: number;
  pendingProductCount: number;
  pendingUnitOfMeasureCount: number;
  pendingTotal: number;
}

export interface MailAccountStatus {
  configured: boolean;
  connected: boolean;
  accountUsername: string | null;
  tenantId: string | null;
  clientId: string | null;
  lastPollAt: string | null;
  lastPollError: string | null;
}

export interface MailConfigInput {
  tenantId: string;
  clientId: string;
  clientSecret: string;
}

export interface AdminState {
  mailAccount: MailAccountStatus;
  tunables: AdminTunables;
  versions: readonly ConfigVersion[];
  customerSync: CustomerSyncStats;
  productSync: ProductSyncStats;
  legalEntitySync: LegalEntitySyncStats;
  siteSync: SiteSyncStats;
  paymentTermsSync: PaymentTermsSyncStats;
  deliveryTermsSync: DeliveryTermsSyncStats;
  deliveryModeSync: DeliveryModeSyncStats;
  unitOfMeasureSync: UnitOfMeasureSyncStats;
  embeddingSync: EmbeddingSyncStats;
}

export interface UploadInboundDocumentResponse {
  document_id: string;
  staging_row_id: string;
  status: string;
  archive_ref: string | null;
}

export interface SyncJobResponse {
  target: string;
  recordCount: number;
  durationMs: number;
  stalenessSeconds: number;
  lastSyncAt: string | null;
  skipped?: boolean;
  skipReason?: string;
}

export interface StageRetryResponse {
  id: string;
  state: string;
  mode: 'single' | 'from_here';
  waitingStatus: string;
}

export interface CockpitApi {
  health(): Promise<HealthResponse>;
  queue(opts?: { includeResolved?: boolean }): Promise<readonly QueueRow[]>;
  queueViewConfig(): Promise<QueueViewConfig>;
  exceptions(): Promise<readonly ExceptionRow[]>;
  exception(id: string): Promise<ExceptionDetailRow>;
  saveOperatorResolution(
    id: string,
    body: {
      addedLines?: readonly ManualLineAddition[];
      fields: Record<string, string>;
      selectedProducts: Record<string, string>;
      selectedUnits: Record<string, string>;
      selectedQuantities: Record<string, string>;
      excludedLines: Record<string, { category: string; reason: string }>;
      /**
       * ADO #700: edit-time justifications keyed by reasonKey. The cockpit
       * collects one entry per modified key field; the backend rejects the
       * save if any modified key field is missing a reason.
       */
      fieldReasons: Record<string, string>;
      feedback?: string;
    },
  ): Promise<ExceptionDetailRow>;
  submitExceptionCorrection(
    id: string,
    body: { fields: Record<string, string>; feedback: string; action: 'resume' | 'close' },
  ): Promise<ExceptionDetailRow>;
  forfeitOrder(
    id: string,
    body: { reason: string; skipCustomerAcknowledgement: boolean },
  ): Promise<ExceptionDetailRow>;
  applyDuplicateDecision(
    id: string,
    body: { decision: 'duplicate' | 'not_duplicate' },
  ): Promise<ExceptionRow>;
  drafts(opts?: { includeApproved?: boolean }): Promise<readonly DraftRow[]>;
  approveDraft(
    id: string,
    input: {
      requestedShippingDate: string | null;
      requestedDeliveryDate: string | null;
      shippingDateConfirmed: boolean;
      deliveryReviewRevision?: string;
      paymentTermsOverride?: string;
      paymentTermsOverrideReason?: string;
    },
  ): Promise<DraftRow>;
  rejectDraft(id: string, reason: string): Promise<DraftRow>;
  feedback(): Promise<readonly FeedbackEntry[]>;
  deleteFeedback(id: string): Promise<{ deleted: number }>;
  deleteAllFeedback(): Promise<{ deleted: number }>;
  adminState(): Promise<AdminState>;
  saveMailConfig(values: MailConfigInput): Promise<AdminState>;
  resetMail(): Promise<AdminState>;
  updateTunables(values: AdminTunables): Promise<AdminState>;
  activateVersion(id: string): Promise<AdminState>;
  uploadInboundDocument(file: File): Promise<UploadInboundDocumentResponse>;
  deleteQueueItem(id: string): Promise<void>;
  searchProducts(id: string, query: string): Promise<readonly ProductOption[]>;
  searchCustomers(
    id: string,
    query: string,
    legalEntity?: string | null,
    role?: CustomerAccountRole,
  ): Promise<readonly CustomerOption[]>;
  retryStage(
    id: string,
    body: {
      mode: 'single' | 'from_here';
      stageKey: TimelineStageKey;
      substepId?: string | null;
    },
  ): Promise<StageRetryResponse>;
  runCustomerSync(): Promise<SyncJobResponse>;
  runProductSync(): Promise<SyncJobResponse>;
  runExactProductSync(itemNumber: string): Promise<SyncJobResponse>;
  runLegalEntitySync(): Promise<SyncJobResponse>;
  runSiteSync(): Promise<SyncJobResponse>;
  runPaymentTermsSync(): Promise<SyncJobResponse>;
  runDeliveryTermsSync(): Promise<SyncJobResponse>;
  runDeliveryModeSync(): Promise<SyncJobResponse>;
  runUnitOfMeasureSync(): Promise<SyncJobResponse>;
  runEmbeddingSync(): Promise<SyncJobResponse>;
  resetSync(target: string): Promise<{ ok: boolean; target: string }>;
  /** Trigger the D365 startBatchJob OData action bound to
   * tpmDI_DirectSalesOrders. Returns `{ status, dataAreaId }` so the UI
   * can confirm the action fired. */
  triggerStagingBatchJob(dataAreaId: string): Promise<{ status: string; dataAreaId: string }>;
}

export function createCockpitApi(client: AxiosInstance): CockpitApi {
  return {
    async health() {
      const { data } = await client.get<HealthResponse>('/healthz');
      return data;
    },
    async queue(opts) {
      const params: Record<string, string> = {};
      if (opts?.includeResolved) params.includeResolved = 'true';
      const { data } = await client.get<readonly QueueRow[]>('/api/v1/cockpit/queue', { params });
      return data;
    },
    async queueViewConfig() {
      const { data } = await client.get<QueueViewConfig>('/api/v1/cockpit/queue-view-config');
      return data;
    },
    async exceptions() {
      const { data } = await client.get<readonly ExceptionRow[]>('/api/v1/cockpit/exceptions');
      return data;
    },
    async exception(id) {
      const { data } = await client.get<ExceptionDetailRow>(`/api/v1/cockpit/exceptions/${id}`);
      return data;
    },
    async searchProducts(id, query) {
      const { data } = await client.get<readonly ProductOption[]>(
        `/api/v1/cockpit/queue/${id}/products/search`,
        {
          params: { q: query },
        },
      );
      return data;
    },
    async searchCustomers(id, query, legalEntity, role = 'ship_to') {
      const { data } = await client.get<readonly CustomerOption[]>(
        `/api/v1/cockpit/queue/${id}/customers/search`,
        {
          params: { q: query, role, ...(legalEntity ? { legalEntity } : {}) },
        },
      );
      return data;
    },
    async saveOperatorResolution(id, body) {
      const { data } = await client.post<ExceptionDetailRow>(
        `/api/v1/cockpit/exceptions/${id}/save`,
        body,
      );
      return data;
    },
    async submitExceptionCorrection(id, body) {
      const { data } = await client.post<ExceptionDetailRow>(
        `/api/v1/cockpit/exceptions/${id}/corrections`,
        body,
      );
      return data;
    },
    async forfeitOrder(id, body) {
      const { data } = await client.post<ExceptionDetailRow>(
        `/api/v1/cockpit/exceptions/${id}/forfeit`,
        body,
      );
      return data;
    },
    async applyDuplicateDecision(id, body) {
      const { data } = await client.post<ExceptionRow>(
        `/api/v1/cockpit/exceptions/${id}/duplicate-decision`,
        body,
      );
      return data;
    },
    async drafts(opts) {
      const params: Record<string, string> = {};
      if (opts?.includeApproved) params.includeApproved = 'true';
      const { data } = await client.get<readonly DraftRow[]>('/api/v1/cockpit/drafts', {
        params,
      });
      return data;
    },
    async approveDraft(id, input) {
      const approvalPath = `/api/v1/cockpit/drafts/${id}/approve`;
      let response = await client.post<DraftRow>(approvalPath, input);
      if (response.status !== 202) return response.data;
      // Keep the operator's original approval intent while the existing
      // pipeline validates the corrected data. No second user action is needed.
      for (let attempt = 0; attempt < 180; attempt += 1) {
        await new Promise<void>((resolve) => setTimeout(resolve, 2_000));
        const { data: row } = await client.get<{
          state: string;
          d365StagingError?: string | null;
          warnings?: readonly string[];
        }>(`/api/v1/cockpit/exceptions/${id}`);
        if (row.state === 'Awaiting Operator Approval') {
          response = await client.post<DraftRow>(approvalPath, input);
          if (response.status === 202) {
            throw new Error('D365 could not validate the selected shipping setup. No order was created.');
          }
          return response.data;
        }
        if (['Validation Failed', 'Exception - Awaiting Operator', 'Worker Error', 'Closed'].includes(row.state)) {
          throw new Error(row.d365StagingError || row.warnings?.[0] || 'D365 could not validate the current order data.');
        }
      }
      throw new Error('D365 validation did not finish within the expected time. No approval was completed.');
    },
    async rejectDraft(id, reason) {
      const { data } = await client.post<DraftRow>(`/api/v1/cockpit/drafts/${id}/reject`, {
        reason,
      });
      return data;
    },
    async feedback() {
      const { data } = await client.get<readonly FeedbackEntry[]>('/api/v1/cockpit/feedback');
      return data;
    },
    async deleteFeedback(id) {
      const { data } = await client.delete<{ deleted: number }>(`/api/v1/cockpit/feedback/${id}`);
      return data;
    },
    async deleteAllFeedback() {
      const { data } = await client.delete<{ deleted: number }>('/api/v1/cockpit/feedback');
      return data;
    },
    async adminState() {
      const { data } = await client.get<AdminState>('/api/v1/cockpit/admin');
      return data;
    },
    async saveMailConfig(values) {
      const { data } = await client.post<AdminState>('/api/v1/cockpit/admin/mail/config', values);
      return data;
    },
    async resetMail() {
      const { data } = await client.post<AdminState>('/api/v1/cockpit/admin/mail/reset');
      return data;
    },
    async updateTunables(values) {
      const { data } = await client.post<AdminState>('/api/v1/cockpit/admin/tunables', values);
      return data;
    },
    async activateVersion(id) {
      const { data } = await client.post<AdminState>(
        `/api/v1/cockpit/admin/versions/${id}/activate`,
      );
      return data;
    },
    async uploadInboundDocument(file) {
      const form = new FormData();
      form.append(
        'envelope',
        JSON.stringify({
          source: 'cockpit:manual-upload',
          source_message_id: `${file.name}-${crypto.randomUUID()}`,
          received_at: new Date().toISOString(),
          subject: file.name,
        }),
      );
      form.append('raw', file, file.name);
      const { data } = await client.post<UploadInboundDocumentResponse>(
        '/api/v1/inbound-documents',
        form,
      );
      return data;
    },
    async deleteQueueItem(id) {
      await client.delete(`/api/v1/cockpit/queue/${id}`);
    },
    async retryStage(id, body) {
      const { data } = await client.post<StageRetryResponse>(
        `/api/v1/cockpit/queue/${id}/retry`,
        body,
      );
      return data;
    },
    async runCustomerSync() {
      const { data } = await client.post<SyncJobResponse>('/api/v1/cockpit/admin/sync/customers');
      return data;
    },
    async runProductSync() {
      const { data } = await client.post<SyncJobResponse>('/api/v1/cockpit/admin/sync/products');
      return data;
    },
    async runExactProductSync(itemNumber) {
      const { data } = await client.post<SyncJobResponse>(
        '/api/v1/cockpit/admin/sync/products/exact',
        { itemNumber },
      );
      return data;
    },
    async runLegalEntitySync() {
      const { data } = await client.post<SyncJobResponse>(
        '/api/v1/cockpit/admin/sync/legal-entities',
      );
      return data;
    },
    async runSiteSync() {
      const { data } = await client.post<SyncJobResponse>('/api/v1/cockpit/admin/sync/sites');
      return data;
    },
    async runPaymentTermsSync() {
      const { data } = await client.post<SyncJobResponse>(
        '/api/v1/cockpit/admin/sync/payment-terms',
      );
      return data;
    },
    async runDeliveryTermsSync() {
      const { data } = await client.post<SyncJobResponse>(
        '/api/v1/cockpit/admin/sync/delivery-terms',
      );
      return data;
    },
    async runDeliveryModeSync() {
      const { data } = await client.post<SyncJobResponse>(
        '/api/v1/cockpit/admin/sync/delivery-modes',
      );
      return data;
    },
    async runUnitOfMeasureSync() {
      const { data } = await client.post<SyncJobResponse>(
        '/api/v1/cockpit/admin/sync/units-of-measure',
      );
      return data;
    },
    async runEmbeddingSync() {
      const { data } = await client.post<SyncJobResponse>('/api/v1/cockpit/admin/sync/embeddings');
      return data;
    },
    async resetSync(target) {
      const { data } = await client.post<{ ok: boolean; target: string }>(
        `/api/v1/cockpit/admin/sync/${target}/reset`,
      );
      return data;
    },
    async triggerStagingBatchJob(dataAreaId) {
      const { data } = await client.post<{ status: string; dataAreaId: string }>(
        '/api/v1/cockpit/admin/trigger-staging-batch',
        { dataAreaId },
      );
      return data;
    },
  };
}
